import { action, observable, runInAction } from "mobx";

import { Entity } from "@libs/api/hub/Entity.ts";
import { EntityEventData } from "@libs/api/hub/EntityEventData.ts";
import { EventAction } from "@libs/api/hub/EventAction.ts";
import { IHubGateway } from "@libs/api/hub/HubGateway.ts";
import {
  XeroAccountMappingsAddDto,
  XeroAccountMappingsDto,
  XeroFetchedAccountDto,
  XeroFinancialSyncAllRequestDto,
  XeroFinancialSyncGetRequestArgsDto,
  XeroFinancialSyncGetRequestDto,
  XeroFinancialSyncGetRequestStatusType,
  XeroSettingsAddDto,
  XeroSettingsDto,
  XeroSettingsUpdateDto,
  XeroTenantDto,
  XeroTenantInfoDto
} from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { ICustomIntegrationXeroGateway } from "@libs/gateways/custom-integration/ICustomIntegrationXeroGateway.interface.ts";
import type { IRootStore } from "@shared-types/root/root-store.interface.ts";
import type { Store } from "@stores/types/store.type.ts";
import { mergeModel } from "@stores/utils/store.utils.ts";

import { CustomIntegrationXeroRef } from "./CustomIntegrationXeroRef.ts";
import { CustomIntegrationXeroUi } from "./CustomIntegrationXeroUi.ts";
import { AccountMapping } from "./models/AccountMapping.ts";

export class CustomIntegrationXeroStore
  implements Store<CustomIntegrationXeroStore, CustomIntegrationXeroRef>
{
  constructor(
    private gateway: ICustomIntegrationXeroGateway,
    public hub: IHubGateway
  ) {}
  root: IRootStore;
  ui = new CustomIntegrationXeroUi(this);

  afterAttachRoot() {
    this.hub.onEntityEvent(Entity.XeroTenantUpdate, this.onUpdateXeroTenant);
    this.hub.onEntityEvent(Entity.XeroFinancialSync, this.onXeroFinancialSync);
  }

  accountMappingMap = observable.map<string, AccountMapping>();

  @observable
  public tenantId: string | undefined;

  @observable
  public tenants: XeroTenantDto[] | undefined;

  @observable
  public settings: XeroSettingsDto | undefined;

  @observable
  getFinancialSyncRequestStatus?: XeroFinancialSyncGetRequestStatusType;

  @action
  public setGetFinancialSyncRequestStatus(
    getFinancialSyncRequestStatus:
      | XeroFinancialSyncGetRequestStatusType
      | undefined
  ) {
    this.getFinancialSyncRequestStatus = getFinancialSyncRequestStatus;
  }

  @action
  private mergeAccountMapping = (dto: XeroAccountMappingsDto) => {
    return mergeModel({
      dto,
      map: this.accountMappingMap,
      getNewModel: () => new AccountMapping(dto)
    });
  };

  @action
  private onUpdateXeroTenant = (event: EntityEventData) => {
    // we don't get etag from BE right now (it might be done in the future)
    if (
      event.action === EventAction.Create ||
      event.action === EventAction.Update
    ) {
      this.ui.recentlyUpdatedXeroTenant = event.key;
    } else if (event.action === EventAction.Delete) {
      this.ui.recentlyUpdatedXeroTenant = `${event.key}-deleted`;
    }
  };

  private onXeroFinancialSync = async (event: EntityEventData) => {
    if (event.action === EventAction.Create && event.key === "ACCOUNTS") {
      runInAction(() => {
        this.ui.recentlyUpdatedXeroTenant = event.id;
        this.getFinancialSyncRequestStatus =
          XeroFinancialSyncGetRequestStatusType.PENDING;
      });
    } else if (
      event.action === EventAction.Update &&
      event.key === "ACCOUNTS"
    ) {
      if (event.id === this.ui.recentlyUpdatedXeroTenant) {
        const getFinancialSyncRequest = await this.getFinancialSync(event.id);
        if (getFinancialSyncRequest.status !== undefined) {
          if (
            getFinancialSyncRequest.status ===
              XeroFinancialSyncGetRequestStatusType.PENDING ||
            getFinancialSyncRequest.status ===
              XeroFinancialSyncGetRequestStatusType.INTERNALFAILURE
          ) {
            runInAction(() => {
              this.getFinancialSyncRequestStatus =
                XeroFinancialSyncGetRequestStatusType.INTERNALFAILURE;
            });
          } else {
            runInAction(() => {
              this.getFinancialSyncRequestStatus =
                XeroFinancialSyncGetRequestStatusType.COMPLETED;
              this.ui.recentlyUpdatedXeroTenant = undefined;
            });
          }
        }
      }
    } else {
      runInAction(() => {
        this.getFinancialSyncRequestStatus = undefined;
        this.ui.recentlyUpdatedXeroTenant = undefined;
      });
    }
  };

  @action
  private setTenants(tenants: XeroTenantDto[] | undefined) {
    this.tenants = tenants;
  }

  async addXeroTenant(authorizationCode: string): Promise<XeroTenantInfoDto> {
    const tenantInfo = await this.gateway.addXeroTenant(authorizationCode);
    this.setTenantId(tenantInfo.tenantId);
    this.setTenants(tenantInfo.tenants);
    return tenantInfo;
  }

  @action
  private setTenantId(tenantId: string | undefined) {
    this.tenantId = tenantId;
  }

  async getXeroTenantInfo(): Promise<XeroTenantInfoDto> {
    const tenantInfo = await this.gateway.getXeroTenantInfo();
    this.setTenantId(tenantInfo.tenantId);
    this.setTenants(tenantInfo.tenants);
    return tenantInfo;
  }

  async setXeroTenant(tenantId: string): Promise<void> {
    await this.gateway.setXeroTenant(tenantId);
    this.setTenantId(tenantId);
  }

  async getFinancialSyncAll(): Promise<XeroFinancialSyncAllRequestDto> {
    return this.gateway.postFinancialSyncAll();
  }

  async postFinancialSync(
    syncGetType: string
  ): Promise<XeroFinancialSyncGetRequestDto> {
    return this.gateway.postFinancialSync(syncGetType);
  }

  async getFinancialSync(
    requestId: string
  ): Promise<XeroFinancialSyncGetRequestDto> {
    return this.gateway.getFinancialSync(requestId);
  }

  async getFinancialByArgsSync(
    targetTenantId: XeroFinancialSyncGetRequestArgsDto
  ): Promise<XeroFinancialSyncGetRequestDto> {
    return this.gateway.getFinancialByArgsSync(targetTenantId);
  }

  async getFetchedAccount(): Promise<XeroFetchedAccountDto[]> {
    return this.gateway.getFetchedAccount();
  }

  async getAccountMappings(): Promise<AccountMapping[]> {
    const dtos = await this.gateway.getAccountMappings();
    return dtos.map(this.mergeAccountMapping);
  }

  async addAccountMappings(
    request: XeroAccountMappingsAddDto
  ): Promise<AccountMapping> {
    const mapping = await this.gateway.postAccountMappings(request);
    return this.mergeAccountMapping(mapping);
  }

  async updateAccountMappings(
    request: XeroAccountMappingsDto
  ): Promise<AccountMapping> {
    const mapping = await this.gateway.updateAccountMappings(request);
    return this.mergeAccountMapping(mapping);
  }

  async deleteXeroConnectionTenant(
    tenantId: string
  ): Promise<XeroTenantInfoDto> {
    const tenantInfo = await this.gateway.deleteConnection(tenantId);
    this.setTenantId(tenantInfo.tenantId);
    this.setTenants(tenantInfo.tenants);
    return tenantInfo;
  }

  @action
  private setSettings(settings: XeroSettingsDto) {
    this.settings = settings;
  }

  async getSettings(): Promise<XeroSettingsDto> {
    const result = await this.gateway.getSettings();
    this.setSettings(result);
    return result;
  }

  async addSettings(request: XeroSettingsAddDto): Promise<XeroSettingsAddDto> {
    const result = await this.gateway.addSettings(request);
    this.setSettings(result);
    return result;
  }

  async updateSettings(
    request: XeroSettingsUpdateDto
  ): Promise<XeroSettingsDto> {
    const result = await this.gateway.updateSettings(request);
    this.setSettings(result);
    return result;
  }
}
